import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "ticketino-api-client";
import { GetOrganizerUser } from "../../utils/api";
import { useLanguage } from "../../hooks/useLanguage";
import { useOrder } from "../../hooks/useOrder";
import { toZonedTime } from 'date-fns-tz';

import Header from "../components/Header";
import Footer from "../components/Footer";
import Loading from "../components/Loading";

import previewSvg from "../../images/icons/eye.svg";

const AccountLogin = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordError, setShowPasswordError] = useState(false);

    const [resources, setResources] = useState({});
    const { language } = useLanguage();

    const { isEmailResetSuccess } = useParams();

    const timeZone = 'Europe/Zurich';

    const navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const order = useOrder(baseUrl);

    useEffect(() => {
        loadToken();
    }, []);

    useEffect(() => {
        requestResources();
    }, [language]); //everytime language is changed

    const loadToken = async () => {
        await axios.get("form/token").then((res) => {
            axios.defaults.headers.common["Authorization"] = "Bearer " + res.data;
            api.defaults.headers.common['Authorization'] = "Bearer " + res.data;

            localStorage.setItem("token", res.data);

            requestFormSettings();
        });
    };

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            //setPosId(res.data.posId);
        });
    };

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        try {
            const res = await axios.post('/form/login', {
                username,
                password
            });

            const loggedInOrganizerId = await fetchOrganizerUser(res.data)

            if (!loggedInOrganizerId) {
                setShowPasswordError(true);
                return;
            }

            localStorage.setItem("buyerToken", res.data);
            localStorage.setItem("username", username); // Save username in session
            localStorage.setItem("loggedInOrganizerId", loggedInOrganizerId); // Save the logged in organizer Id

            var dateTimeNow = new Date();
            var zurichDateTimeNow = toZonedTime(dateTimeNow, timeZone)

            localStorage.setItem("loginDateTime", zurichDateTimeNow.toISOString()); // Save login datetime

            navigate(`/${language}/orders`);
        } catch (err) {
            console.error('Login failed:', err.response?.data || err.message);
        }
    };

    const fetchOrganizerUser = async (buyerToken) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchOrganizerUser");
            }
            const data = await GetOrganizerUser(baseUrl, "buyerToken", buyerToken, username);
            const matchedOrganizer = Array.isArray(data)
                ? data.find(org => org.username?.toLowerCase() === username.toLowerCase())
                : null;

            return matchedOrganizer?.id;
        } catch (error) {
            console.error('Error fetching organizer user:', error);
        }
    };

    return (
        <>
            <Header language={language} pagePath="login" pageTitle={resources?.Account_LoginYourAccount}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">

                <div className="container wrapper">

                    {/* navigation */}
                    <div className="row mb-4">
                        <div className="col-6">
                            <button className="btn-custom transparent btn-navigation" type="submit"
                                onClick={() => navigate(`/${language}/account-username`)}>
                                {resources?.Account_LoginRegister}
                            </button>
                        </div>
                        <div className="col-6 text-end">
                            <button className="btn-custom btn-navigation" type="submit">
                                {resources?.Account_LoginLogin}
                            </button>
                        </div>
                    </div>

                    {isEmailResetSuccess === "success" &&
                        <div className="row text-start mt-2 mb-2">
                            <p className="text-success">Ihr Passwort wurde erfolgreich zurückgesetzt.</p>
                        </div>
                    }

                    {/* inputs */}
                    <form onSubmit={handleLogin}>
                        <div className="form-group row">
                            <label htmlFor="staticEmail" className="col-sm-3 col-form-label">{resources?.BuyerEmail}</label>
                            <div className="col-sm-9">
                                <input
                                    className="form-control"
                                    name="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="form-group row">
                            <label htmlFor="inputPassword" className="col-sm-3 col-form-label">{resources?.BuyerPassword}</label>
                            <div className="col-sm-9 position-relative">
                                <div className="password-input-wrapper">
                                    <input
                                        className="form-control"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <img
                                        src={previewSvg}
                                        alt="Show password"
                                        className="password-toggle-eye"
                                        onClick={() => setShowPassword((v) => !v)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                                {showPasswordError &&
                                    <div className="text-danger mt-2 fw-bold">{resources?.Account_LoginPasswordNotCorrect}</div>
                                }
                            </div>
                        </div>

                        {/* infos */}
                        <div className="row mt-4">
                            <div className="col-md-12 d-flex flex-column">
                                <span><b>{resources?.Account_LoginForgotPassword}</b></span>
                                <span>
                                    {resources?._Click}
                                    <button
                                        type="button"
                                        style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => navigate(`/${language}/password-reset/account-login`)}
                                    >
                                        {resources?._Here}
                                    </button>
                                    {resources?.Account_LoginForgotPasswordDesc}
                                </span>
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-md-12 d-flex flex-column">
                                <span><b>{resources?.Account_LoginNotYetRegistered}</b></span>
                                <span>{resources?.Account_LoginNotYetRegisteredDesc}</span>
                            </div>
                        </div>

                        {/* button */}
                        <div className="row mt-5">
                            <div className="col-md-12 text-center">
                                <button className="btn-custom btn-navigation" type="submit">Log In</button>
                            </div>
                        </div>

                    </form>
                </div>
                
            </div>

            <Footer language={language} />
        </>
    )
}

export default AccountLogin